/**
 * Conversation list — the left rail of every messaging surface.
 *
 * Every role now gets the same behaviour: avatar, name, subtitle, last-message
 * preview, relative timestamp, unread count badge, selection highlight, hover
 * state, search, optional filter chips, keyboard navigation (↑/↓/Home/End/Enter)
 * and proper listbox semantics.
 */

import React from 'react';
import { Archive, ArchiveRestore, MessageSquare, Pin, PinOff, Search, SlidersHorizontal, X } from 'lucide-react';
import type { MessagingConversation } from './types';
import {
  ConversationListSkeleton,
  FilterChip,
  IconButton,
  MessagingAvatar,
  MessagingEmptyState,
  MessagingTag,
  UnreadBadge,
} from './primitives';
import { formatRelativeTime, matchesQuery, roleLabel } from './utils';

export interface ConversationListFilter {
  id: string;
  label: string;
  count?: number;
}

export interface ConversationListProps {
  conversations: MessagingConversation[];
  selectedId?: string | null;
  onSelect: (conversation: MessagingConversation) => void;

  title?: string;
  subtitle?: string;
  loading?: boolean;

  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;

  filters?: ConversationListFilter[];
  activeFilterId?: string;
  onFilterChange?: (id: string) => void;

  /** Extra header controls (advanced filters toggle, compose button…). */
  headerActions?: React.ReactNode;
  /** Expanded filter panel rendered under the header. */
  filterPanel?: React.ReactNode;

  /**
   * Per-viewer pin toggle (Chat Phase 4). When provided, every row
   * gains a Pin/Unpin action; the surface owns persistence (the state
   * is the viewer's own and never affects the counterparty).
   */
  onTogglePin?: (conversation: MessagingConversation) => void;
  /** Per-viewer archive toggle (Chat Phase 4) — same contract. */
  onToggleArchive?: (conversation: MessagingConversation) => void;

  emptyTitle?: string;
  emptyDescription?: string;
  /** Set false when the caller already filtered the list. */
  applySearchFilter?: boolean;
  ariaLabel?: string;
}

export const ConversationRow = React.memo(function ConversationRow({
  conversation,
  selected,
  onSelect,
  onTogglePin,
  onToggleArchive,
}: {
  conversation: MessagingConversation;
  selected: boolean;
  onSelect: (conversation: MessagingConversation) => void;
  onTogglePin?: (conversation: MessagingConversation) => void;
  onToggleArchive?: (conversation: MessagingConversation) => void;
}) {
  const unread = conversation.unreadCount || 0;
  const time = formatRelativeTime(conversation.timestamp);
  const pinned = !!conversation.pinned;
  const archived = !!conversation.archived;
  const hasActions = !!(onTogglePin || onToggleArchive);

  const row = (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={`msg-item ${unread ? 'msg-item--unread' : ''} ${pinned ? 'msg-item--pinned' : ''}`.trim()}
      onClick={() => onSelect(conversation)}
      data-conversation-id={conversation.id}
    >
      <MessagingAvatar
        name={conversation.initials || conversation.name}
        photoUrl={conversation.avatarUrl}
        online={conversation.online}
      />

      <span className="msg-item-body">
        <span className="msg-item-top">
          <span className="msg-item-name">
            {pinned && (
              <Pin size={11} className="msg-item-pin" aria-hidden="true" />
            )}
            {conversation.name}
            {pinned && <span className="msg-visually-hidden"> (pinned)</span>}
            {archived && <span className="msg-visually-hidden"> (archived)</span>}
          </span>
          {time && <span className="msg-item-time">{time}</span>}
        </span>

        {conversation.subtitle && <span className="msg-item-sub">{conversation.subtitle}</span>}

        <span className="msg-item-preview">{conversation.preview || 'No messages yet'}</span>

        {(conversation.role || conversation.status || (conversation.tags && conversation.tags.length > 0)) && (
          <span className="msg-item-tags">
            {conversation.role && conversation.role !== 'unknown' && (
              <MessagingTag tone="accent">{roleLabel(conversation.role)}</MessagingTag>
            )}
            {conversation.status && <MessagingTag>{conversation.status.replace(/_/g, ' ')}</MessagingTag>}
            {conversation.tags?.slice(0, 2).map((tag) => (
              <MessagingTag key={tag}>#{tag}</MessagingTag>
            ))}
          </span>
        )}
      </span>

      <span className="msg-item-end">
        <UnreadBadge count={unread} />
      </span>
    </button>
  );

  if (!hasActions) return row;

  /* Pin/archive actions are SIBLINGS of the row button, never children:
     interactive content inside a <button> is invalid HTML and breaks
     assistive-tech interaction models. The wrap positions them over
     the row's right edge; they reveal on hover and on focus-within so
     keyboard users reach them in tab order right after the row. */
  return (
    <div className="msg-item-wrap" role="presentation">
      {row}
      <span className="msg-item-actions">
        {onTogglePin && (
          <IconButton
            label={pinned ? `Unpin conversation with ${conversation.name}` : `Pin conversation with ${conversation.name}`}
            small
            active={pinned}
            onClick={() => onTogglePin(conversation)}
          >
            {pinned ? <PinOff size={14} /> : <Pin size={14} />}
          </IconButton>
        )}
        {onToggleArchive && (
          <IconButton
            label={archived ? `Unarchive conversation with ${conversation.name}` : `Archive conversation with ${conversation.name}`}
            small
            active={archived}
            onClick={() => onToggleArchive(conversation)}
          >
            {archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
          </IconButton>
        )}
      </span>
    </div>
  );
});

export function ConversationList({
  conversations,
  selectedId,
  onSelect,
  title = 'Messages',
  subtitle,
  loading = false,
  search = '',
  onSearchChange,
  searchPlaceholder = 'Search conversations…',
  filters,
  activeFilterId,
  onFilterChange,
  headerActions,
  filterPanel,
  emptyTitle = 'No conversations yet',
  emptyDescription = 'New messages will appear here.',
  applySearchFilter = true,
  ariaLabel = 'Conversations',
  onTogglePin,
  onToggleArchive,
}: ConversationListProps) {
  const listRef = React.useRef<HTMLDivElement>(null);

  const visible = React.useMemo(() => {
    if (!applySearchFilter || !search.trim()) return conversations;
    return conversations.filter((c) =>
      matchesQuery(search, c.name, c.subtitle, c.preview, c.status, ...(c.tags || []))
    );
  }, [conversations, search, applySearchFilter]);

  /* Keyboard navigation across rows — ↑/↓/Home/End move, Enter/Space selects. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('.msg-item') || []
    );
    if (rows.length === 0) return;
    const currentIndex = rows.findIndex((row) => row === document.activeElement);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex = Math.min(rows.length - 1, currentIndex + 1);
    if (event.key === 'ArrowUp') nextIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = rows.length - 1;
    if (nextIndex !== currentIndex && rows[nextIndex]) {
      event.preventDefault();
      rows[nextIndex].focus();
    }
  };

  return (
    <div className="msg-list">
      <div className="msg-list-header">
        <div className="msg-list-title-row">
          <h2 className="msg-list-title">
            <MessageSquare size={17} className="msg-title-icon" />
            {title}
          </h2>
          {headerActions}
        </div>
        {subtitle && (
          <p className="msg-item-sub" style={{ marginTop: 2 }}>
            {subtitle}
          </p>
        )}

        {onSearchChange && (
          <div className="msg-search">
            <Search size={15} className="msg-search-icon" />
            <input
              className="msg-search-input"
              type="search"
              value={search}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={(event) => onSearchChange(event.target.value)}
            />
            {search && (
              <span className="msg-search-clear">
                <IconButton label="Clear search" small onClick={() => onSearchChange('')}>
                  <X size={14} />
                </IconButton>
              </span>
            )}
          </div>
        )}

        {filters && filters.length > 0 && onFilterChange && (
          <div className="msg-filters" role="tablist" aria-label="Conversation filters">
            {filters.map((filter) => (
              <FilterChip
                key={filter.id}
                label={filter.label}
                count={filter.count}
                active={activeFilterId === filter.id}
                onClick={() => onFilterChange(filter.id)}
              />
            ))}
          </div>
        )}

        {filterPanel}
      </div>

      <div
        ref={listRef}
        className="msg-list-scroll"
        role="listbox"
        aria-label={ariaLabel}
        onKeyDown={handleKeyDown}
      >
        {loading && conversations.length === 0 ? (
          <ConversationListSkeleton />
        ) : visible.length === 0 ? (
          <MessagingEmptyState
            title={search.trim() ? 'No matches' : emptyTitle}
            description={
              search.trim() ? 'Try a different name, program or keyword.' : emptyDescription
            }
            icon={<Search size={26} strokeWidth={1.75} />}
          />
        ) : (
          visible.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              selected={selectedId === conversation.id}
              onSelect={onSelect}
              onTogglePin={onTogglePin}
              onToggleArchive={onToggleArchive}
            />
          ))
        )}
      </div>
    </div>
  );
}

export { SlidersHorizontal as FilterToggleIcon };
